// src/clock/JobCheckIn.js
//
// UI for job check-in / check-out. Logic lives in ./jobVisits.js.
//
//   <ActiveJobBanner jobVisits={jobVisits} />          top of Home: current stop + timer
//   <JobCheckInButton job={job} jobVisits={jobVisits} /> on each job card / job popup

import React, { useEffect, useState } from "react";
import moment from "moment";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  JOB_VISITS,
  getVisitOccurrenceKey,
  formatElapsed,
  formatMinutes,
  toDateSafe,
  useNow,
  visitMinutes,
  mapsLinkFor,
  FAR_FROM_JOB_METERS,
} from "./jobVisits";
import "./jobCheckIn.css";

const timeText = (v) => {
  const d = toDateSafe(v);
  return d ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";
};

const distanceText = (m) => {
  if (m == null) return null;
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1609.34).toFixed(1)} mi`;
};

// Running timer for one open visit.
const RunningTimer = ({ visit, className = "" }) => {
  const now = useNow(1000, !!visit);
  const start = toDateSafe(visit?.checkInAt);
  if (!start) return null;
  return <span className={`jci-timer ${className}`}>{formatElapsed(now - start.getTime())}</span>;
};

// ---------------------------------------------------------------------------
export const JobCheckInButton = ({ job, jobVisits, compact = false }) => {
  if (!job || !jobVisits) return null;

  const { isCheckedInTo, visitsForJob, checkIn, checkOut, isBusyFor, isBusy, openVisit } = jobVisits;
  const here = isCheckedInTo(job);
  const busy = isBusyFor(job);
  const past = visitsForJob(job).filter((v) => v.status === "checked_out");
  const totalMins = past.reduce((sum, v) => sum + visitMinutes(v), 0);

  return (
    <div className={`jci-wrap ${compact ? "compact" : ""}`} onClick={(e) => e.stopPropagation()}>
      {here ? (
        <>
          <div className="jci-status in">
            <span className="jci-dot" /> Checked in {timeText(openVisit.checkInAt)} ·{" "}
            <RunningTimer visit={openVisit} />
          </div>
          <button
            type="button"
            className="jci-btn out"
            disabled={busy}
            onClick={() => checkOut("user")}
          >
            {busy ? "Saving…" : "Check Out"}
          </button>
        </>
      ) : (
        <button
          type="button"
          className="jci-btn in"
          disabled={busy || isBusy}
          onClick={() => checkIn(job)}
          title="Records your arrival time and location for this job"
        >
          {busy ? "Getting location…" : past.length ? "Check In Again" : "Check In"}
        </button>
      )}

      {past.length > 0 && (
        <div className="jci-history">
          {past.length === 1
            ? `Time on this job: ${formatMinutes(totalMins)} (${timeText(past[0].checkInAt)}–${timeText(past[0].checkOutAt)})`
            : `Time on this job: ${formatMinutes(totalMins)} over ${past.length} visits`}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
export const ActiveJobBanner = ({ jobVisits, isClockedIn }) => {
  const [showToday, setShowToday] = useState(false);
  if (!jobVisits) return null;

  const { openVisit, todaysVisits, checkOut, isBusy, error } = jobVisits;
  const now = Date.now();
  const totalToday = todaysVisits.reduce((sum, v) => sum + visitMinutes(v, now), 0);

  return (
    <div className="jci-banner">
      {openVisit ? (
        <div className="jci-banner-row">
          <div className="jci-banner-main">
            <div className="jci-banner-label">
              <span className="jci-dot" /> Checked in at
            </div>
            <div className="jci-banner-site">
              {openVisit.siteName || "Job"}
              {openVisit.wo ? <span className="jci-wo"> WO #{openVisit.wo}</span> : null}
            </div>
            <div className="jci-banner-sub">
              Since {timeText(openVisit.checkInAt)}
              {openVisit.checkInDistanceMeters != null &&
                openVisit.checkInDistanceMeters > FAR_FROM_JOB_METERS && (
                  <span className="jci-warn">
                    {" "}· {distanceText(openVisit.checkInDistanceMeters)} from job address
                  </span>
                )}
            </div>
          </div>
          <RunningTimer visit={openVisit} className="big" />
          <button
            type="button"
            className="jci-btn out"
            disabled={isBusy}
            onClick={() => checkOut("user")}
          >
            {isBusy ? "Saving…" : "Check Out"}
          </button>
        </div>
      ) : (
        <div className="jci-banner-empty">
          {isClockedIn
            ? "Not checked in to a job. Tap Check In on a job when you arrive."
            : "Clock in for the day, then check in at each job when you arrive."}
        </div>
      )}

      {error && <div className="jci-error">{error}</div>}

      {todaysVisits.length > 0 && (
        <div className="jci-today">
          <button type="button" className="jci-link" onClick={() => setShowToday((v) => !v)}>
            {showToday ? "▾" : "▸"} Today's stops: {todaysVisits.length} · {formatMinutes(totalToday)} on jobs
          </button>
          {showToday && (
            <table className="jci-table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {todaysVisits.map((v) => (
                  <tr key={v.id}>
                    <td>
                      {v.siteName || "Job"}
                      {v.wo ? <div className="jci-muted">WO #{v.wo}</div> : null}
                    </td>
                    <td>{timeText(v.checkInAt)}</td>
                    <td>{v.status === "checked_in" ? "—" : timeText(v.checkOutAt)}</td>
                    <td>
                      {v.status === "checked_in" ? (
                        <RunningTimer visit={v} />
                      ) : (
                        formatMinutes(visitMinutes(v))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small read-only list used by supervisors (job popup / dashboards).
export const VisitList = ({ visits = [], showWorker = true, showJob = false }) => {
  const now = useNow(30000, visits.some((v) => v.status === "checked_in"));
  if (!visits.length) return <div className="jci-muted">No check-ins yet.</div>;
  const sorted = [...visits].sort(
    (a, b) => (toDateSafe(a.checkInAt)?.getTime() || 0) - (toDateSafe(b.checkInAt)?.getTime() || 0)
  );
  return (
    <table className="jci-table">
      <thead>
        <tr>
          {showWorker && <th>Worker</th>}
          {showJob && <th>Job</th>}
          <th>Date</th>
          <th>In</th>
          <th>Out</th>
          <th>Time</th>
          <th>Where</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((v) => {
          const far = v.checkInDistanceMeters != null && v.checkInDistanceMeters > FAR_FROM_JOB_METERS;
          return (
            <tr key={v.id}>
              {showWorker && <td>{v.userName || "—"}</td>}
              {showJob && (
                <td>
                  {v.siteName || "Job"}
                  {v.wo ? <div className="jci-muted">WO #{v.wo}</div> : null}
                  {v.checkOutReason && v.checkOutReason !== "user" && (
                    <div className="jci-muted">
                      {v.checkOutReason === "clock_out"
                        ? "auto: clocked out"
                        : v.checkOutReason === "switched_jobs"
                        ? "auto: checked in elsewhere"
                        : v.checkOutReason === "job_completed"
                        ? "auto: job completed"
                        : v.checkOutReason}
                    </div>
                  )}
                </td>
              )}
              <td>{toDateSafe(v.checkInAt) ? moment(toDateSafe(v.checkInAt)).format("MM/DD") : "—"}</td>
              <td>{timeText(v.checkInAt)}</td>
              <td>{v.status === "checked_in" ? <em>on site</em> : timeText(v.checkOutAt)}</td>
              <td>{formatMinutes(visitMinutes(v, now))}</td>
              <td>
                {mapsLinkFor(v.checkInLocation) ? (
                  <a href={mapsLinkFor(v.checkInLocation)} target="_blank" rel="noreferrer">
                    Map
                  </a>
                ) : (
                  "—"
                )}
                {v.checkInDistanceMeters != null && (
                  <span className={far ? "jci-warn" : "jci-muted"}>
                    {" "}
                    {distanceText(v.checkInDistanceMeters)}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ---------------------------------------------------------------------------
// Staff view inside the job popup: every check-in on this job (all workers).
// Collapsed by default; only listens to Firestore while open.
export const JobVisitHistory = ({ job, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [visits, setVisits] = useState(null);
  const jobId = job?.id;

  useEffect(() => {
    if (!open || !jobId) return undefined;
    const unsub = onSnapshot(
      query(collection(db, JOB_VISITS), where("jobId", "==", jobId)),
      (snap) => setVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.warn("Job visit history failed:", err);
        setVisits([]);
      }
    );
    return () => unsub();
  }, [open, jobId]);

  if (!jobId) return null;

  const occ = getVisitOccurrenceKey(job);
  const list = visits || [];
  const thisDay = list.filter((v) => (v.occurrenceDate || "") === occ);
  const shown = thisDay.length ? thisDay : list;
  const totalMins = shown.reduce((sum, v) => sum + visitMinutes(v), 0);

  return (
    <div className="jci-history-box">
      <button type="button" className="jci-link" onClick={() => setOpen((v) => !v)}>
        {open ? "▾" : "▸"} Check-ins
        {visits ? ` (${shown.length}${shown.length ? ` · ${formatMinutes(totalMins)}` : ""})` : ""}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          {visits === null ? (
            <div className="jci-muted">Loading…</div>
          ) : (
            <>
              {thisDay.length === 0 && list.length > 0 && (
                <div className="jci-muted">No check-ins for {occ || "this date"} — showing all dates.</div>
              )}
              <VisitList visits={shown} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default JobCheckInButton;
